import { isAxiosError } from 'axios';
import React, { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { FormDialog, RhfTextField, toast } from '@core/components';
import type { Design } from '../../api/design';
import {
  type DesignPreset,
  createDesignPreset,
  updateDesignPreset,
} from '../../api/designPreset';
import { useAppContext } from '../context/AppContext';
import {
  type DesignPresetFormValues,
  type PresetFeatures,
  designPresetFormToPayload,
  designPresetToFormValues,
  getDesignPresetCollarVestment,
} from '../utils/designPreset';
import { DesignPresetFields } from './DesignPresetFields';

interface DesignPresetDialogProps {
  open: boolean;
  onClose: () => void;
  design: Design;
  features: PresetFeatures;
  preset?: DesignPreset | null;
  onSuccess?: (preset: DesignPreset) => void;
}

const DesignPresetDialog: React.FC<DesignPresetDialogProps> = ({
  open,
  onClose,
  design,
  features,
  preset,
  onSuccess,
}) => {
  const { vestments } = useAppContext();
  const vestment = useMemo(
    () => getDesignPresetCollarVestment(vestments, design.vestmentType),
    [vestments, design.vestmentType]
  );
  const defaultValues = useMemo(
    () => designPresetToFormValues(preset),
    [preset]
  );

  const onSubmit = async (
    data: DesignPresetFormValues,
    methods: UseFormReturn<DesignPresetFormValues>
  ) => {
    const payload = designPresetFormToPayload(data, features);
    try {
      const saved = preset
        ? await updateDesignPreset(preset.id, payload)
        : await createDesignPreset({ ...payload, design: design.id });
      toast.success(preset ? 'Preset updated' : `"${payload.name}" added`);
      onSuccess?.(saved);
    } catch (error) {
      const nameError: string | undefined = isAxiosError(error)
        ? error.response?.data?.errors?.name
        : undefined;
      if (nameError) {
        methods.setError('name', { message: nameError });
      } else {
        toast.apiError('Failed to save preset.', error);
      }
      throw error;
    }
  };

  return (
    <FormDialog<DesignPresetFormValues>
      open={open}
      onClose={onClose}
      title={
        preset ? `Edit ${design.code} Preset` : `Add ${design.code} Preset`
      }
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={preset ? 'Update' : 'Add'}
    >
      {(methods) => (
        <>
          <RhfTextField<DesignPresetFormValues>
            name="name"
            label="Name"
            required
          />
          <DesignPresetFields
            methods={methods}
            features={features}
            vestment={vestment}
          />
        </>
      )}
    </FormDialog>
  );
};

export default DesignPresetDialog;
